import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Logo1ClinHorizontal } from '../components/Logo1Clin';

export default function TermsAndConditions() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300">
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-8">
          <Logo1ClinHorizontal width={140} />
          <button onClick={() => navigate('/')} className="flex items-center gap-1.5 text-sm text-teal-400 hover:text-teal-300 transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>

        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Terms & Conditions</h1>
        <p className="text-sm text-slate-500 mb-8">Effective Date: March 15, 2026</p>

        <div className="space-y-8 text-sm leading-relaxed">
          <section>
            <h2 className="text-lg font-semibold text-white mb-3">1. General Provisions</h2>
            <p>These Terms and Conditions govern the use of 1clin.com (the "Platform"), owned and operated by <strong className="text-white">Oneprick Labs Private Limited</strong>, having its registered office at Shop No. 14, KH 17/2 & 9, Sukhi Ram Park, Uttam Nagar, New Delhi, 110059, India.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">2. Terms & Conditions: For Doctors (Healthcare Providers)</h2>
            <p className="text-xs text-slate-500 mb-3 italic">Focus: Professional Compliance, Clinical Liability, and Subscription Terms.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">2.1 Professional Eligibility</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>You must be a Registered Medical Practitioner (RMP) holding a valid registration from the National Medical Commission (NMC) or a State Medical Council.</li>
              <li>You are required to provide your registration number and clinic details during onboarding. Oneprick Labs reserves the right to verify these credentials and terminate accounts of non-qualified users.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">2.2 Subscription & Strict No-Refund Policy</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong className="text-white">Billing:</strong> All fees are collected in advance via PayU.</li>
              <li><strong className="text-white">All Sales Are Final:</strong> Oneprick Labs Private Limited maintains a strict NO-REFUND policy. Once a subscription fee is paid, it is non-refundable and non-transferable under any circumstances, including partial use of the month/year or dissatisfaction with software features.</li>
              <li><strong className="text-white">Automatic Renewal:</strong> Unless cancelled 24 hours before the billing cycle, subscriptions will automatically renew.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">2.3 Clinical Responsibility & EMR</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>1Clin is a practice management tool and not a medical device. It does not provide medical advice, diagnosis, or treatment.</li>
              <li>The Doctor is 100% responsible for the accuracy of clinical notes, dosages, and prescriptions generated.</li>
              <li>You agree to comply with the Telemedicine Practice Guidelines, 2020, when using remote consultation features.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">2.4 Data Fiduciary Duties (DPDP Act 2023)</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Under the Digital Personal Data Protection Act, 2023, the Doctor acts as the Data Fiduciary.</li>
              <li>You must obtain explicit and informed consent from patients before entering their personal or health data into 1Clin.</li>
              <li>You are responsible for managing patient requests regarding their data rights.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">2.5 Staff Access & Security</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>You must create unique sub-accounts for clinic staff. Sharing "Doctor" login credentials with assistants is a violation of these terms. You are liable for all actions taken by your staff on the platform.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">3. Terms & Conditions: For Patients (End Users)</h2>
            <p className="text-xs text-slate-500 mb-3 italic">Focus: Platform Role, Emergency Disclaimer, and Data Consent.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">3.1 Nature of Platform</h3>
            <p>1Clin is a technology provider for your Doctor. We do not provide medical services. Your medical relationship and any grievances regarding treatment are strictly with your Healthcare Provider.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">3.2 Emergency Disclaimer</h3>
            <p>1Clin is NOT for life-threatening emergencies. In case of a medical emergency, please visit the nearest physical hospital or call emergency services (102/108) immediately.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">3.3 Accuracy of Information</h3>
            <p>You are responsible for providing an accurate medical history to your doctor. Oneprick Labs is not liable for errors in your digital records caused by inaccurate patient input.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">3.4 Communication Consent</h3>
            <p>By using this platform, you consent to receive appointment reminders, digital prescriptions, and health updates via SMS, Email, or WhatsApp.</p>
          </section>

          <section>
            <h2 className="text-lg font-semibold text-white mb-3">4. Terms & Conditions: For Vendors (Labs/Pharmacies)</h2>
            <p className="text-xs text-slate-500 mb-3 italic">Focus: Fulfillment Standards, Commercial Terms, and Anti-Spam.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">4.1 Service Fulfilment</h3>
            <p>Vendors must fulfill orders received through the 1Clin ecosystem in strict compliance with the Drugs and Cosmetics Act, 1940, and other relevant Indian diagnostic laws.</p>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">4.2 Commercial Payments & No-Refunds</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>All platform listing fees or lead-generation fees paid via PayU are non-refundable.</li>
              <li>Any disputes regarding payments between the Vendor and the Doctor must be settled independently; Oneprick Labs acts only as a facilitator.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">4.3 Data Confidentiality & Anti-Spam</h3>
            <ul className="list-disc pl-5 space-y-1">
              <li>Vendors receive patient data strictly for the purpose of order fulfilment.</li>
              <li>Using patient or doctor contact details for independent marketing, unsolicited calls, or spam is strictly prohibited and will result in immediate termination of the vendor account.</li>
            </ul>

            <h3 className="text-sm font-semibold text-slate-200 mt-4 mb-2">4.4 Intellectual Property</h3>
            <p>Vendors shall not use the "1Clin" or "Oneprick Labs" name, trademark, or logo for their own marketing without express written permission.</p>
          </section>

          <section className="bg-slate-900/50 rounded-xl p-5 border border-slate-800/50">
            <h2 className="text-lg font-semibold text-white mb-3">Statutory Contact Information</h2>
            <p className="mb-2">In accordance with the Information Technology Act, 2000, and DPDP Act, 2023:</p>
            <ul className="space-y-1">
              <li><strong className="text-slate-200">Company:</strong> Oneprick Labs Private Limited</li>
              <li><strong className="text-slate-200">CIN:</strong> U86905DL2023PTC420110</li>
              <li><strong className="text-slate-200">Registered Office:</strong> Shop at Plot No. 14, KH 17/2 & 9, Sukhi Ram Park, Uttam Nagar, New Delhi - 110059.</li>
              <li><strong className="text-slate-200">Grievance Officer:</strong> Vikram Dagar (onepricklabs@gmail.com)</li>
            </ul>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-slate-800 text-center text-xs text-slate-600">
          &copy; {new Date().getFullYear()} Oneprick Labs Private Limited. All rights reserved.
        </div>
      </div>
    </div>
  );
}
